import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useBranding } from "../hooks/useBranding";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import { 
  Users, Banknote, Briefcase, GraduationCap, CalendarClock, 
  LayoutDashboard, Settings, Sparkles, LogOut, Menu, Clock,
  CreditCard, Building2, UserCog, Calendar, MapPin, FileText,
  Network, UserCircle, FolderOpen, Rocket, Laptop,
  BarChart3, FileBarChart, Headphones, ShieldAlert, Target,
  ClipboardList, Megaphone, Gift, Brain, Shield, BookOpen
} from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin_empresa: "Admin Empresa",
  gerente_rh: "Gerente RH",
  supervisor: "Supervisor",
  empleado: "Empleado"
};

const ROLE_COLORS = {
  super_admin: "bg-red-100 text-red-700",
  admin_empresa: "bg-violet-100 text-violet-700",
  gerente_rh: "bg-blue-100 text-blue-700",
  supervisor: "bg-amber-100 text-amber-700",
  empleado: "bg-emerald-100 text-emerald-700"
};

const getMenuItems = (role) => {
  const allItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/portal", label: "Mi Portal", icon: UserCircle, color: "text-indigo-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/analytics", label: "Analytics", icon: BarChart3, color: "text-fuchsia-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/reports", label: "Informes IA", icon: FileBarChart, color: "text-violet-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/employees", label: "Empleados", icon: Users, color: "text-emerald-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor"] },
    { path: "/organigrama", label: "Organigrama", icon: Network, color: "text-teal-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor"] },
    { path: "/payroll", label: "Nómina", icon: Banknote, color: "text-blue-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/recruitment", label: "Reclutamiento", icon: Briefcase, color: "text-violet-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor"] },
    { path: "/psychometric", label: "Psicometría IA", icon: Brain, color: "text-fuchsia-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/training", label: "Capacitaciones", icon: GraduationCap, color: "text-orange-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/performance", label: "Desempeño", icon: Target, color: "text-rose-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor"] },
    { path: "/time-tracking", label: "Control Horario", icon: Clock, color: "text-cyan-600", roles: [] },
    { path: "/attendance", label: "Asistencia", icon: CalendarClock, color: "text-rose-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor"] },
    { path: "/vacations", label: "Vacaciones", icon: Calendar, color: "text-teal-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/onboarding", label: "Onboarding", icon: Rocket, color: "text-pink-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/documents", label: "Documentos", icon: FolderOpen, color: "text-amber-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/assets", label: "Activos", icon: Laptop, color: "text-slate-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/knowledge-base", label: "Manual", icon: BookOpen, color: "text-sky-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/helpdesk", label: "Helpdesk", icon: Headphones, color: "text-sky-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/complaints", label: "Denuncias", icon: ShieldAlert, color: "text-red-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/surveys", label: "Encuestas", icon: ClipboardList, color: "text-indigo-600", roles: ["super_admin", "admin_empresa", "gerente_rh"] },
    { path: "/communications", label: "Comunicación", icon: Megaphone, color: "text-amber-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
    { path: "/benefits", label: "Beneficios", icon: Gift, color: "text-emerald-600", roles: ["super_admin", "admin_empresa", "gerente_rh", "supervisor", "empleado"] },
  ];
  
  return allItems.filter(item => item.roles.includes(role));
};

const getAdminItems = (role) => {
  const items = [];
  
  if (["super_admin", "admin_empresa"].includes(role)) {
    items.push({ path: "/users", label: "Usuarios", icon: UserCog, color: "text-slate-600" });
    items.push({ path: "/billing", label: "Planes y Pagos", icon: CreditCard, color: "text-green-600" });
    items.push({ path: "/company", label: "Empresa", icon: Building2, color: "text-slate-600" });
  }
  
  if (role === "super_admin") {
    items.push({ path: "/super-admin", label: "Panel Super Admin", icon: Shield, color: "text-red-600" });
    items.push({ path: "/revenue", label: "Ingresos / MRR", icon: CreditCard, color: "text-green-600" });
  }
  
  return items;
};

const aiAgents = [
  { path: "/ai-agent/recruitment", label: "Agente Reclutamiento", color: "text-violet-500" },
  { path: "/ai-agent/payroll", label: "Agente Nómina", color: "text-violet-500" },
  { path: "/ai-agent/compliance", label: "Agente Compliance", color: "text-violet-500" },
  { path: "/ai-agent/engagement", label: "Agente Engagement", color: "text-violet-500" },
];

const SidebarContent = ({ onNavigate }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const role = user?.role || "empleado";

  const menuItems = getMenuItems(role);
  const adminItems = getAdminItems(role);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo - White Label aware */}
      <div className="p-6 border-b border-slate-200">
        <Link to="/dashboard" className="flex items-center gap-2" onClick={onNavigate} data-testid="sidebar-logo">
          {branding.wl_logo_url ? (
            <img src={branding.wl_logo_url} alt={branding.wl_app_name} className="h-10 w-auto max-w-[180px] object-contain" />
          ) : (
            <div className="h-10 flex items-center">
              <span className="text-lg font-bold" style={{ color: branding.wl_primary_color }}>{branding.wl_app_name}</span>
            </div>
          )}
          {branding.white_label && branding.wl_app_name && branding.wl_logo_url && (
            <span className="text-sm font-bold text-slate-800 truncate">{branding.wl_app_name}</span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 p-4">
        <nav className="space-y-1">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={`sidebar-item ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? '' : item.color || 'text-slate-400'}`} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Admin Section */}
        {adminItems.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 px-3 mb-3">
              <Settings className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Administración</span>
            </div>
            <nav className="space-y-1">
              {adminItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onNavigate}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        {/* AI Agents Section - Only for pro/enterprise or super_admin */}
        {["super_admin", "admin_empresa", "gerente_rh"].includes(role) && (
          <div className="mt-8">
            <div className="flex items-center gap-2 px-3 mb-3">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Agentes IA</span>
            </div>
            <nav className="space-y-1">
              {aiAgents.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onNavigate}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    data-testid={`nav-${item.path.split('/').pop()}`}
                  >
                    <Sparkles className={`w-4 h-4 ${item.color}`} />
                    <span className="text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        {/* Settings */}
        {["super_admin", "admin_empresa", "gerente_rh"].includes(role) && (
          <div className="mt-8">
            <Link
              to="/ai-settings"
              onClick={onNavigate}
              className={`sidebar-item ${location.pathname === '/ai-settings' ? 'active' : ''}`}
              data-testid="nav-ai-settings"
            >
              <Settings className="w-5 h-5 text-slate-400" />
              <span>Configuración IA</span>
            </Link>
          </div>
        )}
      </ScrollArea>

      {/* User Section */}
      <div className="p-4 border-t border-slate-200">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
            ) : (
              <span className="text-emerald-600 font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Usuario'}</p>
            <Badge className={`text-xs ${ROLE_COLORS[role] || 'bg-slate-100'}`}>
              {ROLE_LABELS[role] || role}
            </Badge>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children, title, subtitle, actions }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { branding } = useBranding();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:w-64 lg:flex-col bg-white border-r border-slate-200">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 glass">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {branding.wl_logo_url ? (
              <img src={branding.wl_logo_url} alt={branding.wl_app_name} className="h-8 w-auto max-w-[140px] object-contain" />
            ) : (
              <span className="text-base font-bold" style={{ color: branding.wl_primary_color }}>{branding.wl_app_name}</span>
            )}
          </div>
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarContent onNavigate={() => setSidebarOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className={`lg:pl-64 pt-16 lg:pt-0 ${localStorage.getItem("impersonating") === "true" ? "mt-10" : ""}`}>
        <div className="p-6 lg:p-8">
          {/* Page Header */}
          {title && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  {title}
                </h1>
                {subtitle && <p className="text-slate-600 mt-1">{subtitle}</p>}
              </div>
              {actions && <div className="flex items-center gap-3">{actions}</div>}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
